import React, { useCallback, useEffect, useState } from 'react';
import {
  LEAD_STATUSES, TEMPERATURES, INTERACTION_TYPES, OUTCOME_SUGGESTIONS, OPEN_STATUSES,
  statusInfo, timeAgo, formatDateTime, formatDay, todayStr, followupState, whatsappUrl, telUrl,
  stockLabel, rpcError, isHttpUrl, possiblyAttended, canDiscard, LEAD_CHANNELS, channelInfo,
  APPOINTMENT_OUTCOME, toLocalInput, gmailComposeUrl, needsFirstCall, upcomingAppointment, formatAppointment
} from './config';
import { L, StatusBadge, TestTag, ChannelTag, Chips, Field } from './ui';
import AssignControl from './AssignControl';
import DiscardControl from './DiscardControl';
import FirstCallPanel from './FirstCallPanel';
import { invokeFn } from './ai';
import { LEADS_AI_ENABLED, WA_AGENT_ENABLED } from './config';
import WhatsAppAgentPanel from './WhatsAppAgentPanel';

const EDITABLE = [
  ['customer_name', 'Nombre del cliente'], ['phone', 'Teléfono'], ['email', 'Email'], ['city', 'Localidad'],
  ['vehicle_text', 'Coche de interés'], ['vehicle_plate', 'Matrícula'], ['vehicle_ref', 'Anuncio en la web'],
  ['vehicle_price', 'Precio al contado'], ['vehicle_price_financed', 'Precio financiado'], ['crm_lead_id', 'Referencia en el CRM'], ['crm_url', 'Enlace del CRM'],
  ['customer_message', 'Mensaje del cliente'], ['notes', 'Notas internas']
];
const LONG_FIELDS = ['customer_message', 'notes'];

export default function LeadDetail({ supabase, lead, access, currentUserId, profiles, accessRows, vehicles, columns, afterChange, onBack }) {
  const nameOf = (id) => (profiles.find(p => p.id === id)?.full_name || '').trim() || 'Usuario';
  const isMine = lead.assigned_to === currentUserId;
  const canEdit = access.supervisor || (isMine && access.ok);
  const isOpen = OPEN_STATUSES.includes(lead.status);
  const stock = stockLabel(lead, vehicles, columns);
  const tel = telUrl(lead);
  const wa = whatsappUrl(lead);
  const crmUrl = isHttpUrl(lead.crm_url) ? lead.crm_url : null;
  const webUrl = isHttpUrl(lead.vehicle_ref) ? lead.vehicle_ref : null;
  // Primera llamada pendiente: hasta registrarla, el contacto es solo por llamada (FirstCallPanel).
  const firstCallPending = canEdit && !!lead.assigned_to && isOpen && !lead.first_contact_at && needsFirstCall(lead);
  const appt = upcomingAppointment(lead);

  const [storeName, setStoreName] = useState('');
  useEffect(() => {
    let alive = true;
    supabase.from('organizations').select('name').eq('id', lead.org_id).maybeSingle()
      .then(({ data }) => { if (alive && data) setStoreName(data.name || ''); });
    return () => { alive = false; };
  }, [supabase, lead.org_id]);

  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');

  const run = async (key, fn, okText) => {
    setBusy(key); setErr(''); setMsg('');
    const { error } = await fn();
    setBusy('');
    if (error) { setErr(rpcError(error)); return false; }
    if (okText) setMsg(okText);
    afterChange();
    return true;
  };

  // ---- Historial ----
  const [history, setHistory] = useState(null);
  const loadHistory = useCallback(async () => {
    const [i, e] = await Promise.all([
      supabase.from('lead_interactions').select('id, type, outcome, summary, user_id, created_at').eq('lead_id', lead.id).order('created_at', { ascending: false }),
      supabase.from('lead_events').select('id, kind, from_value, to_value, actor_id, created_at').eq('lead_id', lead.id).order('created_at', { ascending: false })
    ]);
    const items = [
      ...(i.data || []).map(x => ({ ...x, _t: 'i' })),
      ...(e.data || []).map(x => ({ ...x, _t: 'e' }))
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    setHistory(items);
  }, [supabase, lead.id]);
  useEffect(() => { loadHistory(); }, [loadHistory, lead.updated_at]);

  const eventText = (ev) => {
    const who = ev.actor_id ? nameOf(ev.actor_id) : 'Sistema';
    switch (ev.kind) {
      case 'created':
        if ((ev.to_value || '').startsWith('crm:')) return `Entró automáticamente desde el CRM (${ev.to_value.slice(4)})`;
        return `${who} registró el lead${ev.to_value ? ' y se lo quedó' : ''}`;
      case 'claimed': return `${who} lo cogió de la Bandeja`;
      case 'channel': return `${who} cambió el canal: ${channelInfo(ev.from_value).label} → ${channelInfo(ev.to_value).label}`;
      case 'discarded': return `${who} lo descartó${ev.to_value ? ` (${ev.to_value})` : ''}`;
      case 'restored': return `${who} lo recuperó (vuelve a ${statusInfo(ev.to_value).label})`;
      case 'assigned': return `${who} lo asignó a ${nameOf(ev.to_value)}`;
      case 'reassigned': return `${who} lo pasó de ${nameOf(ev.from_value)} a ${nameOf(ev.to_value)}`;
      case 'unassigned': return `${who} lo devolvió a la Bandeja`;
      case 'appointment': return `${who} agendó una cita para el ${ev.to_value ? formatAppointment(ev.to_value) : '—'}`;
      case 'status': return `${who} cambió el estado: ${statusInfo(ev.from_value).label} → ${statusInfo(ev.to_value).label}`;
      default: return `${who}: ${ev.kind}`;
    }
  };

  // Texto corto de un cambio que acompaña a un contacto (sin repetir quién lo hizo).
  const eventShort = (ev) => {
    switch (ev.kind) {
      case 'status': return `Estado: ${statusInfo(ev.from_value).label} → ${statusInfo(ev.to_value).label}`;
      case 'appointment': return `📅 Cita: ${ev.to_value ? formatAppointment(ev.to_value) : '—'}`;
      default: return eventText(ev);
    }
  };

  // Historial agrupado: lo que hace la misma persona en unos segundos (p. ej. registrar la llamada, agendar la cita y el
  // cambio de estado que provoca) sale en una sola entrada.
  const historyGroups = React.useMemo(() => {
    if (!history) return null;
    const groups = [];
    history.forEach(h => {
      const actor = h._t === 'i' ? h.user_id : h.actor_id;
      const t = new Date(h.created_at).getTime();
      const g = groups[groups.length - 1];
      if (g && g.actor === actor && actor && Math.abs(g.t - t) <= 10000) g.items.push(h);
      else groups.push({ key: `${h._t}-${h.id}`, actor, t, at: h.created_at, items: [h] });
    });
    return groups.map(g => ({ ...g, items: [...g.items].reverse() }));
  }, [history]);

  // ---- Estado ----
  const [lostOpen, setLostOpen] = useState(false);
  const [lostReason, setLostReason] = useState('');
  const changeStatus = async (status) => {
    if (!status || status === lead.status) return;
    if (status === 'perdido') { setLostOpen(true); return; }
    await run('status', () => supabase.rpc('set_lead_status', { p_lead_id: lead.id, p_status: status }), `Estado: ${statusInfo(status).label}`);
  };
  const confirmLost = async () => {
    const ok = await run('status', () => supabase.rpc('set_lead_status', { p_lead_id: lead.id, p_status: 'perdido', p_lost_reason: lostReason }), 'Marcado como perdido');
    if (ok) { setLostOpen(false); setLostReason(''); }
  };

  const patch = (key, p, okText) => run(key, () => supabase.rpc('update_lead_details', { p_lead_id: lead.id, p_patch: p }), okText);

  // ---- Registrar contacto ----
  const [iType, setIType] = useState('llamada');
  const [iOutcome, setIOutcome] = useState('');
  const [iSummary, setISummary] = useState('');
  const [iNext, setINext] = useState(todayStr(1));
  const [iAppt, setIAppt] = useState('');
  const withAppt = iType !== 'nota' && iOutcome.trim().toLowerCase() === APPOINTMENT_OUTCOME.toLowerCase();
  const needsNext = iType !== 'nota' && isOpen && !['reservado'].includes(lead.status);
  const logInteraction = async () => {
    if (needsNext && !iNext) { setErr('Indica la fecha del próximo seguimiento.'); return; }
    if (/[<>]/.test(iOutcome + iSummary)) { setErr('No se permiten los caracteres < ni >.'); return; }
    let apptIso = null;
    if (withAppt) {
      const d = new Date(iAppt);
      if (!iAppt || Number.isNaN(d.getTime()) || d.getTime() < Date.now()) { setErr('Indica el día y la hora de la cita (en el futuro).'); return; }
      apptIso = d.toISOString();
    }
    const ok = await run('log', () => supabase.rpc('log_lead_interaction', {
      p_lead_id: lead.id, p_type: iType, p_outcome: iOutcome || null, p_summary: iSummary || null,
      p_next_followup: iType === 'nota' ? null : (iNext || null), p_appointment_at: apptIso
    }), iType === 'nota' ? 'Nota guardada' : 'Contacto registrado');
    if (ok) { setIOutcome(''); setISummary(''); setINext(todayStr(1)); setIAppt(''); loadHistory(); }
  };

  // ---- Editar datos ----
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const startEdit = () => {
    const d = {};
    EDITABLE.forEach(([k]) => { d[k] = lead[k] == null ? '' : String(lead[k]); });
    setDraft(d); setEditing(true);
  };
  const saveEdit = async () => {
    if (!String(draft.customer_name || '').trim()) { setErr('El nombre del cliente es obligatorio.'); return; }
    if (/[<>]/.test(Object.values(draft).join(''))) { setErr('No se permiten los caracteres < ni >.'); return; }
    const p = {};
    EDITABLE.forEach(([k]) => {
      const before = lead[k] == null ? '' : String(lead[k]);
      if (draft[k] !== before) p[k] = (k === 'vehicle_price' || k === 'vehicle_price_financed') ? draft[k].replace(/[€\s]/g, '').replace(/\.(?=\d{3}(\D|$))/g, '').replace(',', '.') : draft[k];
    });
    if (Object.keys(p).length === 0) { setEditing(false); return; }
    if ((p.vehicle_price && !Number.isFinite(Number(p.vehicle_price))) || (p.vehicle_price_financed && !Number.isFinite(Number(p.vehicle_price_financed)))) { setErr('El precio no es un número válido.'); return; }
    const ok = await patch('edit', p, 'Datos guardados');
    if (ok) setEditing(false);
  };

  const fu = followupState(lead);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <button type="button" onClick={onBack} style={{ ...L.secondary, padding: '6px 10px' }}>← Volver</button>
        <h4 style={{ ...L.title, fontSize: '1.1rem' }}>{lead.customer_name}</h4>
        <StatusBadge status={lead.status} />
        <ChannelTag channel={lead.channel} />
        {lead.is_test && <TestTag />}
      </div>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', backgroundColor: 'var(--b-0f172a)',
        border: '1px solid var(--l-f59e0b)', borderRadius: '10px', padding: '8px 14px', marginBottom: '10px' }}>
        <span aria-hidden="true" style={{ fontSize: '1.3rem' }}>📥</span>
        <span style={{ display: 'grid' }}>
          <span style={{ color: 'var(--t-9ca3af)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Entrada del lead</span>
          <strong style={{ color: 'var(--t-f1f5f9)', fontSize: '1.05rem' }}>
            {new Date(lead.received_at).toLocaleString('es-ES', { weekday: 'long', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
          </strong>
        </span>
        <span style={{ color: 'var(--t-f59e0b)', fontSize: '0.85rem', fontWeight: 600 }}>{timeAgo(lead.received_at)}</span>
      </div>

      {appt && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', backgroundColor: 'var(--b-0f172a)',
          border: '1px solid var(--l-6d28d9)', borderRadius: '10px', padding: '8px 14px', margin: '0 0 10px', width: 'fit-content' }}>
          <span aria-hidden="true" style={{ fontSize: '1.3rem' }}>📅</span>
          <span style={{ display: 'grid' }}>
            <span style={{ color: 'var(--t-9ca3af)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Cita</span>
            <strong style={{ color: 'var(--t-f1f5f9)', fontSize: '1.05rem' }}>{formatAppointment(appt)}</strong>
          </span>
          <span style={{ color: 'var(--t-c4b5fd)', fontSize: '0.8rem' }}>
            {lead.appointment_reminded_at ? '🔔 Recordatorio enviado' : '🔔 Recordatorio por correo 2 h antes'}
          </span>
        </div>
      )}

      <p style={{ ...L.muted, margin: '0 0 12px' }}>
        {lead.assigned_to ? <>👤 {isMine ? 'Tuyo' : nameOf(lead.assigned_to)}</> : 'En la Bandeja, sin asignar'}
        {lead.first_contact_at && <> · Primer contacto {formatDateTime(lead.first_contact_at)}</>}
      </p>

      {access.supervisor && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '-4px 0 12px' }}>
          <span style={L.muted}>Canal:</span>
          <Chips small value={lead.channel} disabled={busy === 'channel'}
            options={LEAD_CHANNELS.map(c => ({ key: c.key, label: `${c.icon} ${c.label}` }))}
            onChange={c => c !== lead.channel && run('channel', () => supabase.rpc('set_lead_channel', { p_lead_id: lead.id, p_channel: c }), `Canal cambiado a ${channelInfo(c).label.toLowerCase()}`)} />
        </div>
      )}

      {msg && <div style={L.ok}>{msg}</div>}
      {err && <div style={L.error}>{err}</div>}

      {isOpen && possiblyAttended(lead) && (
        <div style={{ ...L.warn, display: 'grid', gap: '8px' }}>
          <span>
            ⚠️ <strong>Posible lead ya atendido.</strong> Lo creó en el CRM <strong>{lead.crm_originator}</strong>, no el formulario
            de la web (API). Compruébalo en el CRM antes de llamar; si ya lo atiende otra persona, descártalo.
          </span>
          {canDiscard(lead, access, currentUserId) && (
            <div style={{ display: 'flex' }}>
              <DiscardControl supabase={supabase} lead={lead} onDone={() => { setMsg('Lead descartado'); afterChange(); }} />
            </div>
          )}
        </div>
      )}
      {lead.status === 'descartado' && (
        <div style={{ ...L.section, display: 'grid', gap: '8px' }}>
          <span style={{ color: 'var(--t-cbd5e1)', fontSize: '0.9rem' }}>
            🗑️ Descartado{lead.discarded_by ? ` por ${nameOf(lead.discarded_by)}` : ''}{lead.discarded_at ? ` el ${formatDateTime(lead.discarded_at)}` : ''}
            {lead.discard_reason ? `. Motivo: ${lead.discard_reason}` : ''}. No cuenta en las métricas.
          </span>
          {access.supervisor && (
            <div>
              <button type="button" style={L.secondary} disabled={busy === 'restore'}
                onClick={() => run('restore', () => supabase.rpc('restore_lead', { p_lead_id: lead.id }), 'Lead recuperado')}>
                {busy === 'restore' ? 'Recuperando…' : '↩️ Recuperar lead'}
              </button>
            </div>
          )}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
        {tel && <a href={tel} style={L.button}>📞 Llamar</a>}
        {wa && !firstCallPending && <a href={wa} target="_blank" rel="noopener noreferrer" style={L.secondary}>💬 WhatsApp</a>}
        {gmailComposeUrl(lead) && !firstCallPending && <a href={gmailComposeUrl(lead)} target="_blank" rel="noopener noreferrer" style={L.secondary}>✉️ Email (Gmail)</a>}
        {webUrl && <a href={webUrl} target="_blank" rel="noopener noreferrer" style={L.secondary}>🌐 Anuncio en la web</a>}
        {crmUrl && <a href={crmUrl} target="_blank" rel="noopener noreferrer" style={L.secondary}>🔗 Ficha en el CRM</a>}
        {!tel && !lead.email && <span style={L.muted}>Sin teléfono ni email: añádelos en "Datos".</span>}
      </div>

      {/* Asignación */}
      {(!lead.assigned_to || access.supervisor) && isOpen && (
        <div style={L.section}>
          <h5 style={L.sectionTitle}>👤 Asignación</h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'flex-start' }}>
            {!lead.assigned_to && access.can_claim && (
              <button type="button" style={L.button} disabled={busy === 'claim'}
                onClick={() => run('claim', () => supabase.rpc('claim_lead', { p_lead_id: lead.id }), 'Es tuyo: ya está en Mi cartera')}>
                {busy === 'claim' ? 'Cogiendo…' : '✋ Cogerlo'}
              </button>
            )}
            {access.supervisor && (
              <AssignControl compact supabase={supabase} lead={lead} profiles={profiles} accessRows={accessRows} onDone={() => { setMsg('Asignación guardada'); afterChange(); }} />
            )}
            {access.supervisor && lead.assigned_to && (
              <button type="button" style={L.secondary} disabled={busy === 'unassign'}
                onClick={() => run('unassign', () => supabase.rpc('unassign_lead', { p_lead_id: lead.id }), 'Devuelto a la Bandeja')}>
                📥 Devolver a la Bandeja
              </button>
            )}
            {!possiblyAttended(lead) && canDiscard(lead, access, currentUserId) && (
              <DiscardControl supabase={supabase} lead={lead} onDone={() => { setMsg('Lead descartado'); afterChange(); }} />
            )}
          </div>
        </div>
      )}

      {/* Estado y seguimiento */}
      <div style={L.section}>
        <h5 style={L.sectionTitle}>📌 Estado y seguimiento</h5>
        {canEdit && lead.assigned_to && lead.status !== 'descartado' ? (
          <>
            <Field label="Estado" htmlFor="lead-status">
              <select id="lead-status" style={L.input} value={lead.status} disabled={busy === 'status'} onChange={e => changeStatus(e.target.value)}>
                {LEAD_STATUSES.filter(s => s.key !== 'sin_asignar').map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </Field>
            <div style={{ marginBottom: '12px' }}>
              <span style={L.label}>Temperatura</span>
              <Chips small value={lead.temperature} disabled={busy === 'temp'} options={TEMPERATURES}
                onChange={t => patch('temp', { temperature: t })} />
            </div>
            <Field label="Próximo seguimiento" htmlFor="lead-next" hint={fu.key !== 'none' ? fu.label : 'Sin fecha'}>
              <input id="lead-next" type="date" style={L.input} value={lead.next_followup || ''} disabled={busy === 'next'}
                onChange={e => patch('next', { next_followup: e.target.value || null }, 'Seguimiento guardado')} />
            </Field>
          </>
        ) : (
          <p style={{ ...L.muted, margin: 0 }}>
            {statusInfo(lead.status).label} · {TEMPERATURES.find(t => t.key === lead.temperature)?.label}
            {lead.next_followup ? ` · Seguimiento ${formatDay(lead.next_followup)}` : ''}
          </p>
        )}
        {lead.status === 'perdido' && lead.lost_reason && <p style={{ ...L.muted, margin: '8px 0 0' }}>Motivo: {lead.lost_reason}</p>}
        {lostOpen && (
          <div style={{ ...L.warn, marginTop: '10px' }}>
            <label style={{ ...L.label, color: 'inherit' }} htmlFor="lead-lost">¿Por qué se pierde?</label>
            <input id="lead-lost" style={L.input} value={lostReason} onChange={e => setLostReason(e.target.value)}
              placeholder="Compró en otro sitio, no responde, spam…" />
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" style={L.secondary} onClick={() => { setLostOpen(false); setLostReason(''); }}>Cancelar</button>
              <button type="button" style={L.danger} disabled={!lostReason.trim() || busy === 'status'} onClick={confirmLost}>Marcar como perdido</button>
            </div>
          </div>
        )}
      </div>

      {LEADS_AI_ENABLED && <LeadProfile supabase={supabase} lead={lead} canGenerate={canEdit} />}

      {/* Primera llamada (solo llamada) y, después, Registrar contacto */}
      {firstCallPending && (
        <FirstCallPanel supabase={supabase} lead={lead} access={access} currentUserId={currentUserId}
          userName={(profiles.find(p => p.id === currentUserId)?.full_name || '').trim()} storeName={storeName}
          onDone={(text) => { setErr(''); setMsg(text); afterChange(); loadHistory(); }} />
      )}
      {canEdit && lead.assigned_to && !firstCallPending && (
        <div style={L.section}>
          <h5 style={L.sectionTitle}>📝 Registrar contacto</h5>
          <div style={{ marginBottom: '12px' }}>
            <Chips small value={iType} onChange={setIType} options={INTERACTION_TYPES} />
          </div>
          <div style={L.grid2}>
            {iType !== 'nota' && (
              <Field label="Resultado" htmlFor="lead-outcome">
                <input id="lead-outcome" list="lead-outcomes" style={L.input} value={iOutcome} onChange={e => setIOutcome(e.target.value)} autoComplete="off" />
                <datalist id="lead-outcomes">{OUTCOME_SUGGESTIONS.map(o => <option key={o} value={o} />)}</datalist>
              </Field>
            )}
            {withAppt && (
              <Field label="Día y hora de la cita *" htmlFor="lead-iappt" hint="Recordatorio por correo 2 horas de tienda antes.">
                <input id="lead-iappt" type="datetime-local" style={L.input} value={iAppt} min={toLocalInput(new Date())}
                  onChange={e => { setIAppt(e.target.value); if (e.target.value) setINext(e.target.value.slice(0, 10)); }} />
              </Field>
            )}
            {iType !== 'nota' && (
              <Field label={needsNext ? 'Próximo seguimiento *' : 'Próximo seguimiento'} htmlFor="lead-inext">
                <input id="lead-inext" type="date" style={L.input} value={iNext} min={todayStr()} onChange={e => setINext(e.target.value)} />
              </Field>
            )}
          </div>
          <Field label={iType === 'nota' ? 'Nota' : 'Resumen'} htmlFor="lead-summary">
            <textarea id="lead-summary" style={{ ...L.textarea, minHeight: '60px' }} value={iSummary} onChange={e => setISummary(e.target.value)} />
          </Field>
          <p style={{ ...L.muted, margin: '-4px 0 10px' }}>
            El primer contacto registrado (llamada, WhatsApp, email o visita; aunque no conteste) marca el lead como atendido. Las notas no cuentan.
          </p>
          <button type="button" style={L.button} disabled={busy === 'log' || (iType === 'nota' && !iSummary.trim())} onClick={logInteraction}>
            {busy === 'log' ? 'Guardando…' : iType === 'nota' ? 'Guardar nota' : 'Registrar contacto'}
          </button>
        </div>
      )}

      {WA_AGENT_ENABLED && lead.phone_e164 && (
        <WhatsAppAgentPanel supabase={supabase} lead={lead} canEdit={canEdit} onChanged={afterChange} />
      )}

      {/* Datos */}
      <div style={L.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <h5 style={L.sectionTitle}>🧾 Datos del cliente y del coche</h5>
          {canEdit && !editing && <button type="button" style={{ ...L.secondary, padding: '4px 10px', fontSize: '0.8rem' }} onClick={startEdit}>✏️ Editar</button>}
        </div>
        {stock && <div style={{ ...L.ok, marginTop: 0 }}>🔗 Coche en EnMarcha: <strong>{stock}</strong></div>}
        {lead.vehicle_id && <VehicleVideos supabase={supabase} vehicleId={lead.vehicle_id} />}
        {editing ? (
          <>
            <div style={L.grid2}>
              {EDITABLE.filter(([k]) => !LONG_FIELDS.includes(k)).map(([k, label]) => (
                <Field key={k} label={label} htmlFor={`lead-edit-${k}`}>
                  <input id={`lead-edit-${k}`} style={L.input} value={draft[k]} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} autoComplete="off" />
                </Field>
              ))}
            </div>
            {LONG_FIELDS.map(k => (
              <Field key={k} label={EDITABLE.find(e => e[0] === k)[1]} htmlFor={`lead-edit-${k}`}>
                <textarea id={`lead-edit-${k}`} style={{ ...L.textarea, minHeight: '60px' }} value={draft[k]} onChange={e => setDraft(d => ({ ...d, [k]: e.target.value }))} />
              </Field>
            ))}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button type="button" style={L.secondary} onClick={() => setEditing(false)}>Cancelar</button>
              <button type="button" style={L.button} disabled={busy === 'edit'} onClick={saveEdit}>{busy === 'edit' ? 'Guardando…' : 'Guardar'}</button>
            </div>
          </>
        ) : (
          <dl style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, max-content) 1fr', gap: '6px 12px', margin: 0, fontSize: '0.85rem' }}>
            {EDITABLE.map(([k, label]) => (
              <React.Fragment key={k}>
                <dt style={{ color: 'var(--t-9ca3af)' }}>{label}</dt>
                <dd style={{ margin: 0, color: 'var(--t-e5e7eb)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {lead[k] == null || lead[k] === '' ? '—'
                    : (k === 'vehicle_price' || k === 'vehicle_price_financed') ? `${Number(lead[k]).toLocaleString('es-ES')} €`
                    : (k === 'vehicle_ref' || k === 'crm_url') && isHttpUrl(lead[k]) ? <a href={lead[k]} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--t-93c5fd)' }}>{lead[k]}</a>
                    : String(lead[k])}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        )}
      </div>

      {/* Historial (desplegable) */}
      <details style={L.section}>
        <summary style={{ ...L.sectionTitle, cursor: 'pointer', margin: 0 }}>
          🕘 Historial{historyGroups ? ` (${historyGroups.length})` : ''} <span style={{ ...L.muted, fontWeight: 400 }}>· pulsa para ver los cambios</span>
        </summary>
        <div style={{ marginTop: '10px' }}>
          {historyGroups === null ? <p style={L.muted}>Cargando…</p> : historyGroups.length === 0 ? <p style={L.muted}>Sin movimientos todavía.</p> : (
            <div style={{ display: 'grid', gap: '8px' }}>
              {historyGroups.map(g => {
                const contacts = g.items.filter(h => h._t === 'i');
                const events = g.items.filter(h => h._t === 'e');
                return (
                  <div key={g.key} style={{ borderLeft: `3px solid ${contacts.length ? 'var(--l-f59e0b)' : 'var(--l-475569)'}`, paddingLeft: '10px' }}>
                    <div style={{ fontSize: '0.75rem', color: 'var(--t-9ca3af)' }}>{formatDateTime(g.at)}</div>
                    {contacts.map(h => (
                      <div key={h.id} style={{ fontSize: '0.85rem', color: 'var(--t-e5e7eb)' }}>
                        <strong>{INTERACTION_TYPES.find(t => t.key === h.type)?.label || h.type}</strong>
                        {h.outcome ? ` · ${h.outcome}` : ''} — {nameOf(h.user_id)}
                        {h.summary && <div style={{ color: 'var(--t-cbd5e1)', whiteSpace: 'pre-wrap', marginTop: '2px' }}>{h.summary}</div>}
                      </div>
                    ))}
                    {contacts.length > 0 && events.length > 0 && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--t-9ca3af)', marginTop: '2px' }}>{events.map(eventShort).join(' · ')}</div>
                    )}
                    {contacts.length === 0 && events.map(h => (
                      <div key={h.id} style={{ fontSize: '0.85rem', color: 'var(--t-cbd5e1)' }}>{eventText(h)}</div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </details>
    </div>
  );
}

// Vídeos del coche vinculado (bucket privado vehicle-videos, carpeta = id del coche). Solo aparece si hay alguno.
function VehicleVideos({ supabase, vehicleId }) {
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    supabase.storage.from('vehicle-videos').list(vehicleId, { limit: 20, sortBy: { column: 'created_at', order: 'desc' } })
      .then(({ data }) => { if (!cancelled) setFiles((data || []).filter(f => f.id && f.name && !f.name.startsWith('.'))); });
    return () => { cancelled = true; };
  }, [supabase, vehicleId]);
  if (files.length === 0) return null;
  const download = async (f) => {
    setBusy(f.name); setError('');
    const { data, error: err } = await supabase.storage.from('vehicle-videos').createSignedUrl(`${vehicleId}/${f.name}`, 600, { download: f.name });
    setBusy('');
    if (err || !data?.signedUrl) { setError('No se ha podido descargar el vídeo.'); return; }
    window.location.assign(data.signedUrl);
  };
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
      {files.map(f => (
        <button key={f.name} type="button" style={L.secondary} disabled={busy === f.name} onClick={() => download(f)}>
          {busy === f.name ? 'Preparando…' : `🎬 Descargar vídeo${files.length > 1 ? ` (${f.name})` : ''}`}
        </button>
      ))}
      {error && <div style={{ ...L.error, margin: 0 }}>{error}</div>}
    </div>
  );
}

const URGENCY = { alta: '🔥 Alta', media: '🌤️ Media', baja: '❄️ Baja', desconocida: '❔ Desconocida' };

// Perfil del cliente generado por IA (Edge Function lead-profile; se guarda en lead_profiles).
function LeadProfile({ supabase, lead, canGenerate }) {
  const [profile, setProfile] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    let cancelled = false;
    supabase.from('lead_profiles')
      .select('summary, intent, budget_hint, urgency, objections, missing_info, next_best_action, generated_at')
      .eq('lead_id', lead.id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setProfile(data || null); });
    return () => { cancelled = true; };
  }, [supabase, lead.id]);

  const generate = async (force) => {
    setBusy(true); setMsg('');
    const { data, error } = await invokeFn(supabase, 'lead-profile', { lead_id: lead.id, force: !!force });
    setBusy(false);
    if (error) { setMsg(error); return; }
    setProfile(data.profile);
    if (data.cached) setMsg('No hay datos nuevos desde la última vez: el perfil sigue igual.');
  };

  if (profile === undefined) return null;
  if (!profile && !canGenerate) return null;
  const list = (items) => (items && items.length ? (
    <ul style={{ margin: '2px 0 0', paddingLeft: '18px', color: 'var(--t-e5e7eb)' }}>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
  ) : <span style={{ color: 'var(--t-9ca3af)' }}>—</span>);

  return (
    <div style={L.section}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
        <h5 style={{ ...L.sectionTitle, margin: 0 }}>🤖 Perfil del cliente</h5>
        {canGenerate && (
          <button type="button" style={{ ...L.secondary, padding: '4px 10px', fontSize: '0.8rem' }} disabled={busy} onClick={() => generate(false)}>
            {busy ? 'Generando…' : profile ? '↻ Actualizar' : 'Generar con IA'}
          </button>
        )}
      </div>
      {msg && <div style={{ ...L.warn, margin: '10px 0 0' }}>{msg}</div>}
      {!profile ? (
        <p style={{ ...L.muted, margin: '8px 0 0' }}>
          La IA resume qué busca el cliente, sus dudas, lo que falta por saber y la siguiente acción recomendada, a partir de los datos y contactos
          registrados. No se le envían el nombre, el teléfono ni el email del cliente.
        </p>
      ) : (
        <div style={{ display: 'grid', gap: '8px', fontSize: '0.85rem', marginTop: '10px' }}>
          <div style={{ color: 'var(--t-f1f5f9)' }}>{profile.summary}</div>
          <div><span style={{ color: 'var(--t-9ca3af)' }}>Qué quiere: </span><span style={{ color: 'var(--t-e5e7eb)' }}>{profile.intent || '—'}</span></div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px' }}>
            <span><span style={{ color: 'var(--t-9ca3af)' }}>Urgencia: </span><span style={{ color: 'var(--t-e5e7eb)' }}>{URGENCY[profile.urgency] || profile.urgency}</span></span>
            <span><span style={{ color: 'var(--t-9ca3af)' }}>Presupuesto: </span><span style={{ color: 'var(--t-e5e7eb)' }}>{profile.budget_hint || 'no consta'}</span></span>
          </div>
          <div><span style={{ color: 'var(--t-9ca3af)' }}>Dudas u objeciones:</span>{list(profile.objections)}</div>
          <div><span style={{ color: 'var(--t-9ca3af)' }}>Falta por saber:</span>{list(profile.missing_info)}</div>
          <div style={{ ...L.ok, margin: 0 }}>👉 <strong>Siguiente paso:</strong> {profile.next_best_action}</div>
          <div style={{ color: 'var(--t-9ca3af)', fontSize: '0.75rem' }}>
            Generado por IA el {new Date(profile.generated_at).toLocaleString('es-ES', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}: revísalo, puede equivocarse.
          </div>
        </div>
      )}
    </div>
  );
}
